import type {
  DocumentationEdgeInput,
  DocumentationNodeInput,
  DocumentationWarning,
} from './contracts';

export interface TopologyResult {
  ordered: DocumentationNodeInput[];
  incoming: Map<string, DocumentationEdgeInput[]>;
  outgoing: Map<string, DocumentationEdgeInput[]>;
  cyclicNodeIds: Set<string>;
  warnings: DocumentationWarning[];
}

export function orderDocumentationTopology(
  nodes: readonly DocumentationNodeInput[],
  edges: readonly DocumentationEdgeInput[],
): TopologyResult {
  const ids = new Set(nodes.map((node) => node.id));
  const incoming = new Map(nodes.map((node) => [node.id, [] as DocumentationEdgeInput[]]));
  const outgoing = new Map(nodes.map((node) => [node.id, [] as DocumentationEdgeInput[]]));
  for (const edge of edges) {
    if (!ids.has(edge.source) || !ids.has(edge.target)) continue;
    outgoing.get(edge.source)!.push(edge);
    incoming.get(edge.target)!.push(edge);
  }
  const compareNode = (left: DocumentationNodeInput, right: DocumentationNodeInput) =>
    left.name.localeCompare(right.name) || left.id.localeCompare(right.id);
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const degrees = new Map(nodes.map((node) => [node.id, incoming.get(node.id)!.length]));
  const queue = nodes.filter((node) => degrees.get(node.id) === 0).sort(compareNode);
  const ordered: DocumentationNodeInput[] = [];
  while (queue.length) {
    const node = queue.shift()!;
    ordered.push(node);
    for (const edge of [...outgoing.get(node.id)!].sort((a, b) =>
      a.target.localeCompare(b.target),
    )) {
      const degree = degrees.get(edge.target)! - 1;
      degrees.set(edge.target, degree);
      if (degree === 0) {
        queue.push(byId.get(edge.target)!);
        queue.sort(compareNode);
      }
    }
  }
  const cyclicNodeIds = new Set(
    nodes.filter((node) => !ordered.includes(node)).map((node) => node.id),
  );
  ordered.push(...nodes.filter((node) => cyclicNodeIds.has(node.id)).sort(compareNode));
  const warnings: DocumentationWarning[] = [];
  const ambiguous = nodes.filter(
    (node) =>
      outgoing.get(node.id)!.length > 1 &&
      outgoing.get(node.id)!.some((edge) => !edge.label && !edge.condition),
  );
  if (ambiguous.length)
    warnings.push({
      code: 'ambiguous_branch',
      message: 'One or more branches have no label or condition.',
      refs: ambiguous.map((node) => node.id),
    });
  if (cyclicNodeIds.size)
    warnings.push({
      code: 'cycle_documentation_limited',
      message: 'Cycles are listed once in a stable order; repeated execution is not implied.',
      refs: [...cyclicNodeIds],
    });
  return { ordered, incoming, outgoing, cyclicNodeIds, warnings };
}
